import {
    useEffect,
    useState,
} from 'react';
import {
    useInfiniteQuery,
    useQuery,
} from '@tanstack/react-query';

import * as DnsRecordsService from '../services/DnsRecordsService';
import {RecordWriteCapabilities} from '../services/DnsRecordsService';
import {DnsRecordsFilterInput} from '../types';

// FNP-1192 — the query layer.
//
// ⚠️ THE TYPE FILTER IS PART OF THE QUERY KEY, because it is SERVER-SIDE.
//
// This is the one structural difference from the zones module, and it matters.
// Zones filter client-side over the loaded window, so their filter lives
// outside the query key and re-narrows cached rows. `listDnsRecords` takes a
// `filter` argument, so changing the type is a DIFFERENT QUERY against a
// different result set — it must re-fetch from page one, not re-narrow what is
// loaded. Leaving it out of the key would serve the previous type's rows.
export const dnsRecordsKeys = {
    all: ['dns-records'] as const,
    lists: () => [...dnsRecordsKeys.all, 'list'] as const,
    // ⚠️ ALL THREE PARTS ARE IN THE KEY. connectionId and zoneId are both in a
    // record list's identity — the same zone id under two connections is two
    // different zones — and the filter selects a different server-side result.
    list: (connectionId?: string, zoneId?: string, filter?: DnsRecordsFilterInput) =>
        [...dnsRecordsKeys.lists(), {
            connectionId,
            zoneId,
            type: filter?.type ?? null,
            name: filter?.name ?? null,
        }] as const,
    capabilities: (connectionId: string) =>
        [...dnsRecordsKeys.all, 'capabilities', connectionId] as const,
    zoneName: (connectionId: string, zoneId: string) =>
        [...dnsRecordsKeys.all, 'zone-name', connectionId, zoneId] as const,
};

// Page size is a SERVER concern under token pagination — fixed by the module,
// never a "rows per page" dropdown.
export const PAGE_SIZE = 50;

// ⚠️ DISABLED UNTIL BOTH IDS ARE RESOLVED. Both are `ID!` on the schema, so
// firing without them is a request guaranteed to fail validation rather than a
// lookup that returns empty.
export const useDnsRecordsInfiniteQuery = (
    connectionId?: string,
    zoneId?: string,
    filter?: DnsRecordsFilterInput,
) => {
    return useInfiniteQuery({
        queryKey: dnsRecordsKeys.list(connectionId, zoneId, filter),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            DnsRecordsService.fetchRecords(
                connectionId as string,
                zoneId as string,
                filter,
                PAGE_SIZE,
                pageParam,
            ),
        // ⚠️ THE ONLY END-OF-RESULTS SIGNAL IS A NULL nextToken.
        //
        // `limit` applies BEFORE the filter on this platform, so a page that
        // comes back with ZERO items and a live token is NORMAL — and with a
        // server-side type filter it is the COMMON case, not an edge one:
        // filtering a busy zone to NS can easily skip several pages of A
        // records before the first match. A `!lastPage.items.length` guard here
        // would stop paging on the first sparse page and hide the delegation
        // this module exists to show.
        //
        // MUST return undefined: null or a repeated token makes React Query
        // refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) {
                return undefined;
            }
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        enabled: Boolean(connectionId) && Boolean(zoneId),
        staleTime: 60_000,
    });
};

// The capability snapshot behind the write-control hiding decision AND the
// create form's type picker. Longer stale time than the record reads: a
// credential's capabilities change when someone re-connects the provider, not
// while a table is being scrolled.
export const useRecordCapabilitiesQuery = (connectionId?: string) => {
    return useQuery({
        queryKey: dnsRecordsKeys.capabilities(connectionId ?? ''),
        // ⚠️ Returns NULL, never undefined, for "no snapshot". React Query v5
        // rejects an undefined resolution outright — it logs "Query data cannot
        // be undefined" and leaves the query neither success nor error. The
        // service says undefined; it is normalised here, at the one boundary
        // that cares.
        queryFn: async (): Promise<RecordWriteCapabilities | null> => {
            if (typeof connectionId === 'undefined' || connectionId === '') {
                throw new Error('connectionId is required parameter');
            }
            return await DnsRecordsService.fetchConnectionCapabilities(connectionId) ?? null;
        },
        enabled: Boolean(connectionId),
        staleTime: 300_000,
    });
};

// The zone's name, for the page heading and the delete confirmation. A record
// list headed by an opaque zone id tells an operator nothing about which zone
// they are editing, which is the misread that makes a delete dangerous.
export const useZoneNameQuery = (connectionId?: string, zoneId?: string) => {
    return useQuery({
        queryKey: dnsRecordsKeys.zoneName(connectionId ?? '', zoneId ?? ''),
        queryFn: async (): Promise<string | null> => {
            if (!connectionId || !zoneId) {
                throw new Error('connectionId and zoneId are required parameters');
            }
            return await DnsRecordsService.fetchZoneName(connectionId, zoneId) ?? null;
        },
        enabled: Boolean(connectionId) && Boolean(zoneId),
        staleTime: 300_000,
    });
};

// ─── MODULE-SCOPED FILTER STORE ───
//
// The filter button, the drawer and the list hook live in DIFFERENT components,
// so a local useState would give each its own copy and the filter would
// silently do nothing. Module-scoped store; every caller subscribes. Same shape
// as the zones module's three stores, so a reader finds one idiom rather than
// two.
//
// ⚠️ ONE FIELD, `type`, AND IT IS SERVER-SIDE. The schema offers exact `name`
// and exact `type` only. `name` is deliberately not exposed as a filter control
// here: an EXACT name match is a strange thing to hand an operator scanning a
// zone (they would have to type the fully-qualified name to see one row), and
// the keyword search over the loaded window serves that need better. Type is
// the filter with a real use — and NS is why.
export type DnsRecordsFilters = {
    type?: string,
}

let globalFilters: DnsRecordsFilters = {};
const filterListeners: Set<(filters: DnsRecordsFilters) => void> = new Set();

const notifyFilterChange = (filters: DnsRecordsFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

// Test-only reset. The store outlives a component tree by design, which means
// it also outlives a test case — without this, a filter set in one test leaks
// into the next.
export const resetDnsRecordsFilters = () => {
    globalFilters = {};
    filterListeners.clear();
};

export const useDnsRecordsFilter = () => {
    const [filters, setFilters] = useState<DnsRecordsFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: DnsRecordsFilters) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        // A consumer mounting after a filter was applied must not start empty.
        setFilters(globalFilters);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    return {
        filters,
        handleSetFilters: (newFilters: DnsRecordsFilters) => notifyFilterChange(newFilters),
        handleClearFilters: () => notifyFilterChange({}),
        // The delegation shortcut the ticket names as the thing to demonstrate.
        // A named function rather than a caller assembling `{type: 'NS'}` by
        // hand, so the one filter value that MEANS something has one call site.
        handleShowDelegation: () => notifyFilterChange({type: 'NS'}),
    };
};
